/**
 * AnswerButton - Shared Answer Button Component
 *
 * Single responsibility: Rendering yes/no answer buttons for prop questions.
 * Extracted from PropPick for reusability.
 */

import React from 'react';
import { useQuestionCardStyle } from '../../contexts/QuestionCardStyleContext';

interface AnswerButtonProps {
  /**
   * The answer this button represents
   */
  answer: 'yes' | 'no' | 'over' | 'under';

  /**
   * Display label for the button
   */
  label: string;

  /**
   * Whether this button is currently selected
   */
  isSelected: boolean;

  /**
   * Popularity percentage to display
   */
  percentage?: number;

  /**
   * Click handler
   */
  onClick: () => void;

  /**
   * Whether the button is disabled
   */
  disabled?: boolean;

  /**
   * Whether the answer was correct (for results mode)
   */
  isCorrect?: boolean;

  /**
   * Test ID for the button
   */
  testId?: string;

  /**
   * Optional secondary image to display in top right corner
   */
  thirdImage?: string;

  /**
   * Whether this is a Daily Double question (for special styling)
   */
  isDailyDouble?: boolean;

  /**
   * Question type for type-specific styling
   */
  questionType?: 'prop' | 'over_under';

  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Answer button for prop questions (Yes/No)
 *
 * @example
 * ```tsx
 * <AnswerButton
 *   answer="yes"
 *   label="Yes"
 *   isSelected={selectedAnswer === 'yes'}
 *   percentage={65}
 *   onClick={() => onAnswerSelect('yes')}
 * />
 * ```
 */
export const AnswerButton: React.FC<AnswerButtonProps> = ({
  answer,
  label,
  isSelected,
  percentage,
  onClick,
  disabled = false,
  isCorrect,
  testId,
  thirdImage,
  isDailyDouble = false,
  questionType,
  className = '',
}) => {
  const { ddButtonBg, ddButtonBorder } = useQuestionCardStyle();
  const hasResult = isCorrect !== undefined;
  const resultBorderClass =
    isSelected && hasResult
      ? isCorrect
        ? 'border-2 border-[#8B5CF6] shadow-[0_0_15px_4px_rgba(139,92,246,0.4)]'
        : 'border-2 border-red-500 shadow-[0_0_15px_4px_rgba(239,68,68,0.4)]'
      : '';

  // Daily Double styling
  const getBorderClass = () => {
    if (hasResult) return resultBorderClass;

    if (isDailyDouble) {
      // Daily Double: blue border default, white on hover/selected
      if (isSelected) return 'border border-white';
      return 'border border-[#0D54AD]';
    }

    // Normal: black border default, gray on selected
    if (isSelected) return 'border border-[#6F6F6F]';
    return 'border border-[#0A0A0C]';
  };

  const getHoverClass = () => {
    if (disabled || hasResult) return '';
    // Daily Double: white border on hover
    // Normal: gray border on hover
    return isDailyDouble ? 'hover:border-white' : 'hover:border-[#6F6F6F]';
  };

  // Background color logic
  const getBackgroundClass = () => {
    if (isSelected ) return 'bg-white';
    if (isDailyDouble) return 'bg-[#000D1F]';
    // Over/under questions use #1A1A20, prop questions use #0A0A0C
    return questionType === 'over_under' ? 'bg-[#1A1A20]' : 'bg-[#0A0A0C]';
  };

  const ddInlineStyle: React.CSSProperties =
    isDailyDouble && !isSelected && !hasResult
      ? {
          ...(ddButtonBg ? { backgroundColor: ddButtonBg } : {}),
          ...(ddButtonBorder ? { borderColor: ddButtonBorder } : {}),
        }
      : {};

  return (
    <button
      data-testid={testId || `prop-answer-${answer}`}
      data-test-result={isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
      data-selected={isSelected ? 'true' : 'false'}
      onClick={onClick}
      disabled={disabled}
      style={ddInlineStyle}
      className={`
        group peer w-full p-4 rounded-xl transition-smooth flex items-center justify-center relative
        ${getBackgroundClass()}
        ${getBorderClass()}
        ${getHoverClass()}
        ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
        ${className}
      `}
    >
      {/* Secondary Image - Top Right */}
      {thirdImage && (
        <div className="absolute top-2 right-2 w-10 h-10">
          <img src={thirdImage} alt="Secondary logo" className="w-full h-full object-contain" />
        </div>
      )}

      {/* Percentage indicator - Bottom Left when there's a logo (avoids collision), else Top Right */}
      {percentage !== undefined && (
        <div
          className={`absolute ${thirdImage ? 'top-2 left-2' : 'top-2 right-2'} flex items-center gap-0.5 px-1 py-0.5 rounded bg-black/30`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-2 h-2"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M20 21a8 8 0 1 0-16 0" />
          </svg>
          <span className="text-white text-xs font-medium">{percentage}%</span>
        </div>
      )}

      {/* Label */}
      <div
        className={`font-bold ${
          label.split(' ').some(l => l.length > 14) ? 'text-md' : 'text-lg'
        } ${isSelected ? 'text-black' : 'text-text-primary'}`}
      >
        {label}
      </div>
    </button>
  );
};
